import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  BookOpenIcon,
  ClipboardCheckIcon,
  FileEditIcon,
  RepeatIcon,
  TrophyIcon,
  UsersIcon,
} from "lucide-react";

import { PageHeader } from "@/components/ui/page-header";
import {
  ActionCard,
  ActionCardGrid,
  ActionCardSection,
} from "@/components/ui/action-card";
import { CuratorAccessAlert } from "@/components/portal/lms/CuratorGate";
import { useLmsCuratorAccess } from "@/hooks/useLmsCuratorAccess";

export const Route = createFileRoute("/portal/lms/")({
  component: LmsDashboard,
});

// Only ever list a card that points at a built page — a card to an unbuilt
// route is a dead link. Cycles and leaderboards are open to every member who
// can open /portal/lms; the curator cards show only to someone who curates at
// least one category (useLmsCuratorAccess — hidden while loading; on error the
// public cards still render beside a retryable alert).
const publicFeatures = [
  {
    title: "Test cycles",
    description:
      "Monthly cycles that assign a published test to every member of a team.",
    icon: RepeatIcon,
    href: "/portal/lms/cycles",
    iconColor: "bg-sky-500",
  },
  {
    title: "Leaderboards",
    description:
      "A team's Top-N and your own rank for a month, overall or per category.",
    icon: TrophyIcon,
    href: "/portal/lms/leaderboard",
    iconColor: "bg-amber-500",
  },
];

const curatorFeatures = [
  {
    title: "Question Curation",
    description:
      "Review AI-drafted questions per category, then approve, edit or delete each — and generate new batches.",
    icon: ClipboardCheckIcon,
    href: "/portal/lms/curation",
    iconColor: "bg-indigo-500",
  },
  {
    title: "Author tests",
    description:
      "Create a test, attach approved questions, and publish it.",
    icon: FileEditIcon,
    href: "/portal/lms/author",
    iconColor: "bg-violet-500",
  },
  {
    title: "Assign tests",
    description:
      "Assign a published test to a learner and their team, with an optional due date.",
    icon: UsersIcon,
    href: "/portal/lms/assign",
    iconColor: "bg-emerald-500",
  },
];

function LmsDashboard() {
  const access = useLmsCuratorAccess();
  const features = access.curatesAny
    ? [...curatorFeatures, ...publicFeatures]
    : publicFeatures;

  return (
    <>
      <Helmet>
        <title>LMS | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="LMS"
          description="Author and assign assessments from your knowledge base, run monthly cycles, and see how teams rank."
          icon={BookOpenIcon}
          eyebrow="Learning"
          gradient
        />

        <CuratorAccessAlert access={access} />

        <ActionCardSection title="Your Services">
          <ActionCardGrid columns={3}>
            {features.map((feature) => (
              <ActionCard
                key={feature.href}
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                href={feature.href}
                iconColor={feature.iconColor}
                testId={`lms-card-${feature.href.split("/").pop()}`}
              />
            ))}
          </ActionCardGrid>
        </ActionCardSection>
      </div>
    </>
  );
}
